import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { InteractiveViewer } from "@/processes/dynamic/InteractiveViewer";
import { extensionOf } from "@/lib/meshFormat";
import {
  NO_ROTATION,
  QUARTER_TURNS_3D,
  quarterTurnsOf,
} from "@/processes/dynamic/interactiveInputs";
import { DxfPreview } from "@/components/DxfPreview";
import { apiFetch } from "@/lib/api";
import { CustomerConfiguratorForm } from "@/processes/dynamic/CustomerConfiguratorForm";
import { lineMaterialSlotDefaults } from "@/processes/dynamic/useCustomerConfiguratorState";
import { sampleCatalog, seedVariantChoices } from "./sampleVariantChoices";
import { summaryFields, summaryLabel, summaryValue } from "./sampleSummary";
import type {
  MachineEntry,
  WizardOperation,
} from "@/pages/onboarding/wizard/model";

const MeshPreview = lazy(() => import("@/components/MeshPreview"));
/** One sample part of the operation (KAN-1555). */
interface SamplePart {
  key: string;
  name: string | null;
  description: string | null;
  file_name: string | null;
  preview_type: string | null;
}

interface SamplePrice {
  available: boolean;
  /** The priced part and what the header shows of it (KAN-1555). */
  sample_key?: string;
  name?: string | null;
  description?: string | null;
  file_name: string | null;
  preview_type: string | null;
  fields: Array<Record<string, unknown>>;
  currency: string | null;
  unit_price: number | null;
  total: number | null;
  /** ``per_piece`` formula outputs, then ``rounding`` (what makes them add up
   *  to the unit price) and ``setup_fee`` (once per order) — KAN-1945. */
  components: Array<{ label: string; amount: number; mode: string }>;
  /** Quantity discount already inside the per-piece amounts, in percent. */
  discount_pct?: number | null;
  /** The ladder it comes from — only the rungs that discount something. */
  discount_tiers?: Array<{ min_qty: number; discount_pct: number }>;
  rates: Array<{ label: string; value: string; unit: string }>;
  /** Why there is no price, when the visitor can act on it
   *  (``quote_only`` — the selection is „na poptávku"). */
  reason?: string | null;
  /** The sample's interactive inputs, resolved like a customer's part
   *  (KAN-1706): the module's recommendation, else the port default. */
  interactive_inputs?: Array<{ control: string; value: unknown }> | null;
  /** Every sample part, primary first — ≥2 = a switcher. */
  samples?: SamplePart[];
}

function rotationOf(inputs: SamplePrice["interactive_inputs"]) {
  const turns = (inputs ?? []).find((i) => i.control === QUARTER_TURNS_3D);
  return quarterTurnsOf(turns?.value) ?? NO_ROTATION;
}

/**
 * KAN-1945 — a catalog rate as people write numbers in the page language:
 * „1,3 ×", „3 000 mm/min", not the database's „1.3000". Whole minutes read
 * better than seconds („5 min", not „300 s").
 */
export function formatRate(value: string, unit: string, lang: string): string {
  const number = Number(value);
  if (value.trim() === "" || !Number.isFinite(number)) {
    return unit ? `${value} ${unit}` : value;
  }
  let shown = number;
  let shownUnit = unit;
  if (unit === "s" && number >= 60 && number % 60 === 0) {
    shown = number / 60;
    shownUnit = "min";
  }
  const text = new Intl.NumberFormat(lang, { maximumFractionDigits: 4 }).format(
    shown,
  );
  return shownUnit ? `${text} ${shownUnit}` : text;
}

/** A material slot of the job: its options depend on the machine (KAN-1945). */
function isMaterialSlot(field: Record<string, unknown>): boolean {
  const source = field.options_source as
    | { material_categories?: unknown }
    | undefined;
  return (
    field.default_line_material === true ||
    (Array.isArray(source?.material_categories) &&
      source.material_categories.length > 0)
  );
}

export function PlatformSamplePrice({
  operation,
  machines,
  materialCodes,
  lang,
  collapsible = false,
  showcase = false,
}: {
  operation: WizardOperation;
  machines: Pick<MachineEntry, "machineKey" | "addonKeys">[];
  materialCodes: string[];
  lang: string;
  /** KAN-1962 — start as the landing's short card (preview, sample parts,
   *  picked options, quantity, price) that unfolds into the full form. */
  collapsible?: boolean;
  /** The landing-page sample: the machines are the platform's pick, so the
   *  page says that a shop chooses its own (KAN-1945). */
  showcase?: boolean;
}): JSX.Element {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(!collapsible);
  const [machineKey, setMachineKey] = useState(machines[0]?.machineKey ?? "");
  const machine =
    machines.find((m) => m.machineKey === machineKey) ?? machines[0];
  const compatible =
    operation.machines.find((m) => m.machine_key === machine?.machineKey)
      ?.material_codes ?? [];
  const materials = operation.materials.filter(
    (m) => materialCodes.includes(m.code) && compatible.includes(m.code),
  );
  // Addons (KAN-1562): an addon fits a BASE machine; a variant takes its
  // parent's. Only the ones fitting the chosen machine are offered and sent.
  const applicableAddons = (key: string | undefined) => {
    const card = operation.machines.find((m) => m.machine_key === key);
    const base = card?.parent_key ?? key ?? "";
    return (operation.addons ?? []).filter((a) =>
      a.applies_to_keys.includes(base),
    );
  };
  const addons = applicableAddons(machine?.machineKey);
  const [addonKeys, setAddonKeys] = useState<string[]>(
    machine?.addonKeys ?? [],
  );
  const chosenAddons = addonKeys.filter((k) =>
    addons.some((a) => a.machine_key === k),
  );
  const pickMachine = (key: string) => {
    const fits = applicableAddons(key).map((a) => a.machine_key);
    const preset = machines.find((m) => m.machineKey === key)?.addonKeys ?? [];
    setAddonKeys(
      [...new Set([...preset, ...addonKeys])].filter((k) => fits.includes(k)),
    );
    setMachineKey(key);
  };
  const [value, setValue] = useState<Record<string, unknown>>({ quantity: 1 });
  const [sampleKey, setSampleKey] = useState("primary");
  const material =
    materials.find((m) => m.code === value.material_code) ?? materials[0];
  const payload = {
    machine_key: machine?.machineKey ?? "",
    addon_keys: chosenAddons,
    material_code: material?.code ?? "",
    quantity: Number(value.quantity ?? 1),
    sample_key: sampleKey,
    configuration: Object.fromEntries(
      Object.entries(value).filter(
        ([k, v]) =>
          k !== "material_code" &&
          k !== "quantity" &&
          ["string", "number", "boolean"].includes(typeof v),
      ),
    ),
  };
  const text = JSON.stringify(payload);
  const [debounced, setDebounced] = useState(text);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(text), 300);
    return () => window.clearTimeout(timer);
  }, [text]);
  const path = `/api/v1/public/onboarding/operations/${encodeURIComponent(operation.slug)}`;
  const query = useQuery<SamplePrice>({
    queryKey: ["public-sample-price", operation.slug, debounced, lang],
    queryFn: () =>
      apiFetch<SamplePrice>(`${path}/sample-price`, {
        method: "POST",
        body: JSON.parse(debounced),
      }),
    enabled: Boolean(machine && material),
    staleTime: 60_000,
    retry: false,
    placeholderData: keepPreviousData,
  });
  const data = query.data;
  const pending = query.isFetching || text !== debounced;
  const money = (amount: number) =>
    new Intl.NumberFormat(lang, {
      style: "currency",
      currency: data?.currency ?? "XXX",
    }).format(amount);
  const percent = (pct: number) =>
    new Intl.NumberFormat(lang, {
      style: "percent",
      maximumFractionDigits: 2,
    }).format(pct / 100);
  const tiers = data?.discount_tiers ?? [];
  const defaults = Object.fromEntries(
    (data?.fields ?? [])
      .filter((f) => typeof f.key === "string" && f.default !== undefined)
      .map((f) => [String(f.key), f.default]),
  );
  // KAN-1559 — a colour / thickness the selected material offers, never free.
  const axisKey = operation.variant_axis?.key ?? null;
  const sampleFields = data?.fields;
  // KAN-1945 — a material slot offers what the chosen machine can load, so a
  // pick made on another machine may not be on offer any more (the server
  // would refuse it). A machine switch starts the slots over.
  const slotMachine = useRef(machineKey);
  useEffect(() => {
    if (slotMachine.current === machineKey) return;
    slotMachine.current = machineKey;
    const slots = (sampleFields ?? [])
      .filter(isMaterialSlot)
      .map((f) => String(f.key));
    if (slots.length === 0) return;
    setValue((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([key]) => !slots.includes(key)),
      ),
    );
  }, [machineKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const seeds = seedVariantChoices(sampleFields ?? [], material, axisKey, {
      ...defaults,
      ...value,
    });
    // KAN-1615 — a material slot starts on the part's own material, as in the
    // shop (KAN-1557); a value the visitor already picked stays.
    for (const [key, code] of Object.entries(
      lineMaterialSlotDefaults({ fields: sampleFields ?? [] }, material?.code ?? ""),
    )) {
      if (!(key in value)) seeds[key] = code;
    }
    if (Object.keys(seeds).length > 0) setValue((prev) => ({ ...prev, ...seeds }));
  }, [sampleFields, material, axisKey, value]); // eslint-disable-line react-hooks/exhaustive-deps
  // The preview follows the part the shown answer is about (KAN-1555).
  const keyQuery = `?key=${encodeURIComponent(data?.sample_key ?? "primary")}`;
  const previewUrl = `${path}/sample-preview${keyQuery}`;
  const fileUrl = `${path}/sample-file${keyQuery}`;
  const samples = data?.samples ?? [];
  const preview = (
    <>
      {data?.preview_type?.startsWith("image/") ? (
        <DxfPreview
          key={previewUrl}
          src={previewUrl}
          alt={data.file_name ?? operation.display_name}
          className="mb-4"
        />
      ) : data?.preview_type === "application/json" ? (
        <InteractiveViewer
          key={previewUrl}
          url={previewUrl}
          sourceUrl={fileUrl}
        />
      ) : data?.file_name &&
        ["stl", "obj", "3mf"].includes(extensionOf(data.file_name)) ? (
        <Suspense fallback={<p>{t("platformSample.loading")}</p>}>
          <MeshPreview
            key={fileUrl}
            src={fileUrl}
            fileName={data.file_name ?? undefined}
            rotation={rotationOf(data.interactive_inputs)}
            height={open ? 240 : 220}
          />
        </Suspense>
      ) : null}
    </>
  );
  const quantity = Number(value.quantity ?? 1);
  const setQuantity = (next: number) =>
    setValue({ ...value, quantity: Math.max(1, Math.min(10000, next)) });
  if (!open) {
    const merged: Record<string, unknown> = {
      ...defaults,
      ...value,
      material_code: material?.code,
    };
    const index = Math.max(
      0,
      samples.findIndex((s) => s.key === (data?.sample_key ?? sampleKey)),
    );
    const step = (delta: number) =>
      setSampleKey(
        samples[(index + delta + samples.length) % samples.length].key,
      );
    const settled = !pending && !query.isPlaceholderData && !query.isError;
    return (
      <section
        className="sw-card"
        data-testid={`platform-sample-${operation.slug}`}
      >
        <div>
          <p className="sw-title" data-testid="platform-sample-name">
            {data?.name ?? data?.file_name ?? t("platformSample.part")}
          </p>
          {data?.description && (
            <p className="sw-desc">{data.description}</p>
          )}
        </div>
        <div className="sw-pv" key={`${operation.slug}:${sampleKey}`}>
          {preview}
          {data?.file_name && (
            <span className="sw-tag">
              {extensionOf(data.file_name).toUpperCase()}
            </span>
          )}
          {samples.length > 1 && (
            <>
              <button
                type="button"
                className="sw-nav prev"
                aria-label={t("platformSample.prevPart")}
                onClick={() => step(-1)}
                data-testid="platform-sample-prev"
              >
                ‹
              </button>
              <button
                type="button"
                className="sw-nav next"
                aria-label={t("platformSample.nextPart")}
                onClick={() => step(1)}
                data-testid="platform-sample-next"
              >
                ›
              </button>
              <div
                className="sw-dots"
                role="group"
                aria-label={t("platformSample.samples")}
              >
                {samples.map((s, i) => (
                  <button
                    key={s.key}
                    type="button"
                    aria-pressed={i === index}
                    aria-label={s.name ?? s.file_name ?? t("platformSample.part")}
                    onClick={() => setSampleKey(s.key)}
                    data-testid={`platform-sample-switch-${s.key}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
        <div className="sw-sum" data-testid="platform-sample-summary">
          {summaryFields(data?.fields).map((f) => (
            <div key={String(f.key)} className="sw-tile">
              <small>{summaryLabel(f, lang)}</small>
              <b>
                {summaryValue(f, merged[String(f.key)], {
                  lang,
                  materials: operation.materials,
                  lineMaterial: material?.display_name ?? null,
                  yesNo: (v) => t(v ? "platformSample.yes" : "platformSample.no"),
                }) ?? "—"}
              </b>
            </div>
          ))}
          <div className="sw-tile sw-qty">
            <button
              type="button"
              aria-label={t("platformSample.less")}
              onClick={() => setQuantity(quantity - 1)}
              data-testid="platform-sample-qty-minus"
            >
              −
            </button>
            <output aria-label={t("platformSample.quantity")}>{quantity}</output>
            <button
              type="button"
              aria-label={t("platformSample.more")}
              onClick={() => setQuantity(quantity + 1)}
              data-testid="platform-sample-qty-plus"
            >
              +
            </button>
          </div>
        </div>
        <div className="sw-price" aria-live="polite">
          <div>
            {pending ? (
              <small>{t("platformSample.loading")}</small>
            ) : settled &&
              Boolean(machine && material) &&
              data?.available &&
              data.total !== null &&
              data.unit_price !== null ? (
              <>
                <small>
                  {t("platformSample.summaryTotal", { count: quantity })}
                </small>
                <b data-testid="platform-sample-total">{money(data.total)}</b>
              </>
            ) : (
              <small>
                {t(
                  settled && data?.reason === "quote_only"
                    ? "platformSample.quoteOnly"
                    : settled && data?.reason === "machine_cannot_carry"
                      ? "platformSample.machineCannotCarry"
                      : "platformSample.unavailable",
                )}
              </small>
            )}
          </div>
          <button
            type="button"
            className="sw-toggle"
            onClick={() => setOpen(true)}
            data-testid="platform-sample-expand"
          >
            {t("platformSample.expand")}
          </button>
        </div>
      </section>
    );
  }
  return (
    <section
      className="rounded-lg border border-border bg-surface p-5"
      data-testid={`platform-sample-${operation.slug}`}
    >
      <h3 className="text-lg font-semibold text-ink">
        {operation.display_name}
      </h3>
      {samples.length > 1 && (
        <div
          role="group"
          aria-label={t("platformSample.samples")}
          className="mb-3 inline-flex flex-wrap rounded border border-border"
          data-testid={`platform-sample-switcher-${operation.slug}`}
        >
          {samples.map((s) => (
            <button
              key={s.key}
              type="button"
              aria-pressed={s.key === sampleKey}
              className={`px-3 py-1 text-sm ${
                s.key === sampleKey
                  ? "bg-accent-bg text-accent-ink"
                  : "text-ink-2"
              }`}
              onClick={() => setSampleKey(s.key)}
              data-testid={`platform-sample-switch-${s.key}`}
            >
              {s.name ?? s.file_name ?? t("platformSample.part")}
            </button>
          ))}
        </div>
      )}
      <div className="mb-4">
        <p className="text-sm text-ink-2" data-testid="platform-sample-name">
          {data?.name ?? data?.file_name ?? t("platformSample.part")}
        </p>
        {data?.description && (
          <p className="text-xs text-ink-2">{data.description}</p>
        )}
      </div>
      {preview}
      <div className="mb-4 grid gap-3 sm:grid-cols-2">
        {/* KAN-1945 — one machine is no choice; the sample just uses it. */}
        {machines.length > 1 && (
          <label className="text-sm">
            {t("platformSample.machine")}
            <select
              className="mt-1 block w-full rounded border border-border bg-bg p-2"
              value={machine?.machineKey ?? ""}
              onChange={(e) => pickMachine(e.target.value)}
              data-testid={`platform-sample-machine-${operation.slug}`}
            >
              {machines.map((m) => (
                <option key={m.machineKey} value={m.machineKey}>
                  {operation.machines.find(
                    (c) => c.machine_key === m.machineKey,
                  )?.name ?? m.machineKey}
                </option>
              ))}
            </select>
            {showcase && (
              <span className="mt-1 block text-xs text-ink-2">
                {t("platformSample.machineNote")}
              </span>
            )}
          </label>
        )}
        <label className="text-sm">
          {t("platformSample.material")}
          <select
            className="mt-1 block w-full rounded border border-border bg-bg p-2"
            value={material?.code ?? ""}
            onChange={(e) =>
              setValue({ ...value, material_code: e.target.value })
            }
          >
            {materials.map((m) => (
              <option key={m.code} value={m.code}>
                {m.display_name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          {t("platformSample.quantity")}
          <input
            className="mt-1 block w-full rounded border border-border bg-bg p-2"
            type="number"
            min={1}
            max={10000}
            value={Number(value.quantity ?? 1)}
            onChange={(e) =>
              setValue({
                ...value,
                quantity: Math.max(
                  1,
                  Math.min(10000, Number(e.target.value) || 1),
                ),
              })
            }
          />
          {tiers.length > 0 && (
            <span
              className="mt-1 block text-xs text-ink-2"
              data-testid="platform-sample-discount-tiers"
            >
              {t("platformSample.discountTiers", {
                tiers: tiers
                  .map((tier) =>
                    t("platformSample.discountTier", {
                      qty: tier.min_qty,
                      pct: percent(tier.discount_pct),
                    }),
                  )
                  .join(", "),
              })}
            </span>
          )}
        </label>
      </div>
      {addons.length > 0 && (
        <fieldset
          className="mb-4 text-sm"
          data-testid={`platform-sample-addons-${operation.slug}`}
        >
          <legend className="mb-1">{t("platformSample.addons")}</legend>
          {addons.map((a) => (
            <label key={a.machine_key} className="flex items-start gap-2 py-1">
              <input
                type="checkbox"
                className="mt-1"
                checked={chosenAddons.includes(a.machine_key)}
                onChange={(e) =>
                  setAddonKeys(
                    e.target.checked
                      ? [...chosenAddons, a.machine_key]
                      : chosenAddons.filter((k) => k !== a.machine_key),
                  )
                }
                data-testid={`platform-sample-addon-${a.machine_key}`}
              />
              <span>
                {a.name}
                {a.description && (
                  <span className="block text-xs text-ink-2">
                    {a.description}
                  </span>
                )}
              </span>
            </label>
          ))}
        </fieldset>
      )}
      {data && (
        <CustomerConfiguratorForm
          schema={{
            fields: data.fields.filter(
              (f) => f.kind !== "material_ref" && f.key !== "quantity",
            ),
          }}
          catalog={sampleCatalog(lang, operation.slug, materials)}
          axisKey={axisKey}
          axisUnit={operation.variant_axis?.unit ?? null}
          analysis={null}
          processType={operation.slug}
          tunables={{}}
          value={{ ...defaults, ...value, material_code: material?.code }}
          onChange={setValue}
          lang={lang}
          livePricing={false}
        />
      )}
      <div aria-live="polite" className="mt-4 border-t border-border pt-4">
        {pending ? (
          <p>{t("platformSample.loading")}</p>
        ) : !query.isPlaceholderData &&
          !query.isError &&
          Boolean(machine && material) &&
          data?.available &&
          data.total !== null &&
          data.unit_price !== null ? (
          <>
            <div className="flex items-baseline justify-between gap-3">
              <span>{t("platformSample.total")}</span>
              <strong className="text-2xl">{money(data.total)}</strong>
            </div>
            <p className="text-sm text-ink-2">
              {t("platformSample.unit", { price: money(data.unit_price) })}
            </p>
            {!!data.discount_pct && (
              <p
                className="mt-2 text-sm text-ink-2"
                data-testid="platform-sample-discount"
              >
                {t("platformSample.discountApplied", {
                  pct: percent(data.discount_pct),
                })}
              </p>
            )}
            <SampleBreakdown
              data={data}
              quantity={Number(value.quantity ?? 1)}
              money={money}
            />
          </>
        ) : !query.isPlaceholderData &&
          !query.isError &&
          data?.reason === "quote_only" ? (
          <p data-testid="platform-sample-quote-only">
            {t("platformSample.quoteOnly")}
          </p>
        ) : !query.isPlaceholderData &&
          !query.isError &&
          data?.reason === "machine_cannot_carry" ? (
          <p data-testid="platform-sample-machine-cannot-carry">
            {t("platformSample.machineCannotCarry")}
          </p>
        ) : (
          <p>{t("platformSample.unavailable")}</p>
        )}
      </div>
      {!!data?.rates.length && (
        <details className="mt-4 text-sm">
          <summary className="cursor-pointer">
            {t("platformSample.rates")}
          </summary>
          {data.rates.map((r, i) => (
            <div key={i} className="mt-2 flex justify-between gap-3">
              <span>{r.label}</span>
              <span>{formatRate(r.value, r.unit, lang)}</span>
            </div>
          ))}
        </details>
      )}
      {collapsible && (
        <button
          type="button"
          className="sw-toggle mt-4"
          onClick={() => setOpen(false)}
          data-testid="platform-sample-collapse"
        >
          {t("platformSample.collapse")}
        </button>
      )}
    </section>
  );
}

/**
 * KAN-1945 — rows a visitor can add up: the per-piece rows plus rounding give
 * the unit price, and unit price × quantity + the one-off set-up give the
 * total. Before, three rows summed to 41 under a total of 42.
 */
function SampleBreakdown({
  data,
  quantity,
  money,
}: {
  data: SamplePrice;
  quantity: number;
  money: (amount: number) => string;
}): JSX.Element {
  const { t } = useTranslation("common");
  const perPiece = data.components.filter((c) => c.mode !== "setup_fee");
  const setup = data.components
    .filter((c) => c.mode === "setup_fee")
    .reduce((sum, c) => sum + c.amount, 0);
  const label = (c: SamplePrice["components"][number]) =>
    c.mode === "rounding"
      ? t("platformSample.rounding")
      : c.mode === "per_piece"
        ? c.label || t("platformSample.unitRow")
        : `${c.label} · ${t("platformSample.setup")}`;
  return (
    <div className="mt-3 text-sm" data-testid="platform-sample-breakdown">
      <p className="text-xs text-ink-2">{t("platformSample.breakdownNote")}</p>
      {perPiece.map((c, i) => (
        <div key={i} className="mt-1 flex justify-between gap-3">
          <span>{label(c)}</span>
          <span>{money(c.amount)}</span>
        </div>
      ))}
      <div className="mt-1 flex justify-between gap-3 border-t border-border pt-1 font-medium">
        <span>{t("platformSample.unitRow")}</span>
        <span>{money(data.unit_price ?? 0)}</span>
      </div>
      {setup > 0 && (
        <div className="mt-2 flex justify-between gap-3">
          <span>{t("platformSample.setupFee")}</span>
          <span>{money(setup)}</span>
        </div>
      )}
      {(quantity > 1 || setup > 0) && data.total !== null && (
        <p className="mt-2 text-xs text-ink-2" data-testid="platform-sample-sum">
          {t(setup > 0 ? "platformSample.sumWithSetup" : "platformSample.sum", {
            qty: quantity,
            unit: money(data.unit_price ?? 0),
            setup: money(setup),
            total: money(data.total),
          })}
        </p>
      )}
    </div>
  );
}
